import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { CognitoUser } from 'amazon-cognito-identity-js';
import {
  confirmCode as apiConfirmCode,
  forgotPassword as apiForgotPwd,
  newPassword as apiNewPwd,
  resendCode as apiResetCode,
  resetPassword as apiResetPwd,
  signIn as apiSignIn,
  SignInResult,
  signUp as apiSignUp,
} from '../authApi';
import SignUp from '../components/SignUp';
import ConfirmCode from '../components/ConfirmCode';
import useAuth from '../authContext';
import SignIn from '../components/SignIn';
import { createUser, IUserTokens } from '../../currentuser/currentUser';
import NewPassword from '../components/NewPassword';
import ResetPassword from '../components/ResetPassword';
import ForgotPasswordInit from '../components/ForgotPasswordInit';
import { analyticsIdentifyUser, analyticsTrack } from '../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppRoutes } from '../../router/AppRoutes';
import { mobileConfig } from '../../commons/config';
import { AuthBannerSwitcher } from '../components/banners/AuthBannerSwitcher';

type AuthScreen =
  | { type: 'SIGN_UP' }
  | { type: 'SIGN_IN' }
  | { type: 'CONFIRM_CODE'; email: string }
  | { type: 'CHANGE_PASSWORD'; cognitoUser: CognitoUser }
  | { type: 'FORGOT_PASSWORD'; email?: string }
  | { type: 'AUTHENTICATED'; tokens: IUserTokens };

const extractOfferDetails = (
  searchParams: URLSearchParams
): { email: string; offer: string } | null => {
  const reactivateParam = searchParams.get('reactivate');
  if (reactivateParam) {
    try {
      const decoded = atob(reactivateParam);
      console.log('AuthContainer. reactivateParam decoded', decoded);

      const [offer, email] = decoded.split(';');
      if (email) {
        return { email, offer };
      }
    } catch (e) {
      console.error('Failed to decode reactivate parameter:', e);
      return null;
    }
  }
  return null;
};

const AuthContainer = (props: { tab: 'SIGN_IN' | 'SIGN_UP' }) => {
  const { tab } = props;
  const [authScreen, setAuthScreen] = useState<AuthScreen>({ type: props.tab });
  const [searchParams] = useSearchParams();
  const [offerDetails, setOfferDetails] = useState<{ email: string; offer: string } | null>(
    extractOfferDetails(searchParams)
  );

  useEffect(() => {
    const extractedOffer = extractOfferDetails(searchParams);
    if (!_.isEqual(extractedOffer, offerDetails)) {
      setOfferDetails(extractedOffer);
    }
  }, [offerDetails, searchParams]);

  const { dispatch } = useAuth();
  useEffect(() => {
    if (authScreen.type === 'AUTHENTICATED') {
      const { tokens } = authScreen;
      dispatch({ type: 'AUTHENTICATED', tokens });
    }
  });

  const navigate = useNavigate();

  const changeTab = (newTab: 'SIGN_IN' | 'SIGN_UP'): void => {
    if (newTab !== tab) {
      if (authScreen.type !== newTab) {
        navigate(newTab === 'SIGN_IN' ? AppRoutes.Auth.signIn : AppRoutes.Auth.signUp, {
          relative: 'route',
        });
      }
    }
  };

  const changeScreen = (newScreen: AuthScreen): void => {
    if (newScreen.type === 'SIGN_UP' || newScreen.type === 'SIGN_IN') {
      changeTab(newScreen.type);
    }
    setAuthScreen(newScreen);
  };

  const signUp = async (email: string, password: string): Promise<void> => {
    analyticsTrack(AnalyticsEvent.USER_SIGN_UP_DETAILS_SUBMITTED);
    const signUpResult = await apiSignUp(email, password);
    switch (signUpResult) {
      case 'OK':
        changeScreen({ type: 'SIGN_IN' });
        break;
      case 'CONFIRM_CODE':
        changeScreen({ type: 'CONFIRM_CODE', email });
        break;
    }
    analyticsTrack(AnalyticsEvent.USER_SIGN_UP_DETAILS_ACCEPTED);
  };

  const confirmCode = async (email: string, code: string): Promise<void> => {
    analyticsTrack(AnalyticsEvent.USER_SIGN_UP_EMAIL_VERIFICATION_SUBMITTED);
    await apiConfirmCode(email, code);
    analyticsTrack(AnalyticsEvent.USER_SIGNED_UP);
    changeScreen({ type: 'SIGN_IN' });
  };

  const handleSignInResult = (signInResult: SignInResult, email: string): void => {
    switch (signInResult.type) {
      case 'OK': {
        const currentUser = createUser(signInResult.tokens);
        if (currentUser && signInResult?.tokens) {
          analyticsIdentifyUser(currentUser, signInResult.tokens);
          changeScreen({ type: 'AUTHENTICATED', tokens: signInResult?.tokens });
        } else {
          throw new Error(
            `Received tokens from cognito on sign-in but couldn't build a current user from them: ${JSON.stringify(
              signInResult.tokens
            )}`
          );
        }
        break;
      }
      case 'CONFIRM_CODE':
        changeScreen({ type: 'CONFIRM_CODE', email });
        break;
      case 'CHANGE_PASSWORD':
        changeScreen({
          type: 'CHANGE_PASSWORD',
          cognitoUser: signInResult.cognitoUser,
        });
        break;
    }
  };

  const signIn = async (email: string, password: string): Promise<void> => {
    const signInResult = await apiSignIn(email, password);
    handleSignInResult(signInResult, email);
  };

  const changePassword = async (password: string, cognitoUser: CognitoUser): Promise<void> => {
    const tokens = await apiNewPwd(password, cognitoUser);
    analyticsTrack(AnalyticsEvent.USER_PASSWORD_CHANGED);
    const currentUser = createUser(tokens);
    if (currentUser) {
      changeScreen({ type: 'AUTHENTICATED', tokens });
    } else {
      throw new Error(
        `Received tokens from cognito on change password but couldn't build a current user from them: ${JSON.stringify(
          tokens
        )}`
      );
    }
  };

  const resetPassword = async (email: string, code: string, password: string): Promise<void> => {
    const signInResult = await apiResetPwd(email, code, password);
    handleSignInResult(signInResult, email);
  };

  const initForgotPassword = async (email: string): Promise<void> => {
    await apiForgotPwd(email);
    changeScreen({ type: 'FORGOT_PASSWORD', email });
  };

  const getComponent = () => {
    switch (authScreen.type) {
      case 'SIGN_UP':
        return (
          <SignUp
            onSignUp={signUp}
            initialEmail={offerDetails?.email}
            offer={offerDetails?.offer}
          />
        );
      case 'CONFIRM_CODE':
        return (
          <ConfirmCode
            onConfirm={async (code) => confirmCode(authScreen.email, code)}
            resend={async () => apiResetCode(authScreen.email)}
          />
        );
      case 'SIGN_IN':
        return (
          <SignIn
            onSignIn={signIn}
            toForgotPassword={() => changeScreen({ type: 'FORGOT_PASSWORD' })}
            initialEmail={offerDetails?.email}
            offer={offerDetails?.offer}
          />
        );
      case 'CHANGE_PASSWORD':
        return (
          <NewPassword
            onSubmit={async (password) => changePassword(password, authScreen.cognitoUser)}
          />
        );
      case 'FORGOT_PASSWORD': {
        const { email } = authScreen;
        return email ? (
          <ResetPassword
            onReset={async (code, password) => resetPassword(email, code, password)}
            resend={async () => apiForgotPwd(email)}
          />
        ) : (
          <ForgotPasswordInit
            onSubmit={initForgotPassword}
            initialEmail={offerDetails?.email}
            offer={offerDetails?.offer}
          />
        );
      }
      case 'AUTHENTICATED':
      default:
        return <></>;
    }
  };

  const growegyMobilePlatform = mobileConfig().growegyMobilePlatform;
  return (
    <div className="auth-container">
      <div className="dialog-container">
        {getComponent()}

        {tab === 'SIGN_IN' ? (
          <div className="auth__sign" onClick={() => changeTab('SIGN_UP')}>
            {growegyMobilePlatform !== 'ios' && (
              <>
                Don’t have an account?{' '}
                <span className="auth__sign-color" data-test="auth__sign-up">
                  Sign up
                </span>
              </>
            )}
          </div>
        ) : (
          <div className="auth__sign" onClick={() => changeTab('SIGN_IN')}>
            Already have an account?{' '}
            <span className="auth__sign-color" data-test="auth__sign-in">
              Sign in
            </span>
          </div>
        )}
      </div>
      {growegyMobilePlatform !== 'ios' && <AuthBannerSwitcher offer={offerDetails?.offer} />}
    </div>
  );
};

export default AuthContainer;
